// import { ILocalCertificate } from '@store/types/commonTypes'

import { ILocalCertificate } from "../types"

const windowLink = window as any

enum CertificateAndHashAlgorithm {
  GOST_94_HASH = '1.2.643.2.2.9',
  GOST_2012_256_HASH = '1.2.643.7.1.1.2.2',
  GOST_2012_512_HASH = '1.2.643.7.1.1.2.3',
  GOST_94 = '1.2.643.2.2.20',
  GOST_2012_256 = '1.2.643.7.1.1.1.1',
  GOST_2012_512 = '1.2.643.7.1.1.1.2'
}

const CadesPluginAlgorithmHash = {
  [CertificateAndHashAlgorithm.GOST_94]: 100,
  [CertificateAndHashAlgorithm.GOST_2012_256]: 101,
  [CertificateAndHashAlgorithm.GOST_2012_512]: 102
}

export default class CryptoProProvider {
  private readonly setError: (errorMessage: string) => void
  cadesplugin = windowLink.cadesplugin

  constructor(setError: any) {
    this.setError = setError
  }

  public algorithmToHash = {
    [CertificateAndHashAlgorithm.GOST_94]: CertificateAndHashAlgorithm.GOST_94_HASH,
    [CertificateAndHashAlgorithm.GOST_2012_256]: CertificateAndHashAlgorithm.GOST_2012_256_HASH,
    [CertificateAndHashAlgorithm.GOST_2012_512]: CertificateAndHashAlgorithm.GOST_2012_512_HASH
  }

  /**
   * signing a Hash File
   */
  public signHash = async (certificate: ILocalCertificate, hash: string, enhanced?: boolean): Promise<any> => {
    // Creating a CAdESCOM.HashedData object
    let oHashedData

    try {
      oHashedData = await this.initializeHashedData(hash, CadesPluginAlgorithmHash[certificate.algorithmValue])
    } catch (e) {
      return e
    }

    // creating a signed message
    // Such a signature is verified by CryptoARM and cryptcp.exe
    return await this.CreateSignature(certificate, oHashedData, enhanced)
  }

  /**
   * we get information from the string, where 'from' is the string itself, 'what' is what we are looking for
   * 'what' - accepts OGRN, OGRNIP, CN and other abbreviations accepted
   * by certificate owner information definition standard
   */
  public getSubjectInfo = (from: string, what: string): string => {
    let info = ''

    const begin = from.indexOf(what)

    if (begin >= 0) {
      const end = from.indexOf(', ', begin)
      info = end < 0 ? from.substr(begin) : from.substr(begin, end - begin)
    }

    return info
  }

  /**
   * get the date according to the locale
   */
  public getDate = (date: string): string => {
    const newDate = new Date(date)
    return newDate.toLocaleDateString()
  }

  /**
   * get a list of all certificates
   */
  public getCertificates = async (cb: (response: any) => void): Promise<any> => {
    const userCerts = await this.getCertificatesFromStore(this.cadesplugin.CADESCOM_CURRENT_USER_STORE, [])
    const containerCerts = await this.getCertificatesFromStore(this.cadesplugin.CADESCOM_CONTAINER_STORE, userCerts)

    const certificates = [...userCerts, ...containerCerts]

    /**
     * pass the received array of certificates to callback
     */
    cb(certificates)
  }

  public readonly getTargetCertificate = async (serialNumber: string): Promise<any> => {
    let targetCert = await this.foundCertInTargetStore(serialNumber, this.cadesplugin.CADESCOM_CONTAINER_STORE)

    if (!targetCert) {
      targetCert = await this.foundCertInTargetStore(serialNumber, this.cadesplugin.CADESCOM_CURRENT_USER_STORE)
    }

    if (!targetCert) {
      this.setError(`Certificate not found: ${serialNumber}`)
      return `Certificate not found: ${serialNumber}`
    }

    return targetCert
  }

  private readonly isCertificateValid = async (certificate: any): Promise<void> => {
    const isValid = await certificate.IsValid();

    const validToDate = await certificate.ValidToDate
    const validFromDate = await certificate.ValidFromDate
    const dateValidToDate = new Date(validToDate);
    const dateValidFromDate = new Date(validFromDate);

    const certName = await certificate.SubjectName

    const now = new Date();
    const isDateValid = now >= dateValidFromDate && now <= dateValidToDate

    if (isValid.Result && isDateValid) {
      return;
    } else {
      throw new Error(`Сертификат "${certName}" недействителен`);
    }
  }

  public readonly getCertificateInBase64 = async (serialNumber: string): Promise<string> => {
    const targetCert = await this.getTargetCertificate(serialNumber)

    await this.isCertificateValid(targetCert);

    const certificateInBase64: string = await targetCert.Export(0)

    return certificateInBase64.replace(/\r?\n|\r|' '/g, '')
  }

  private readonly getCertificatesFromStore = async (
    storeName: string | number,
    certs: ILocalCertificate[]
  ): Promise<any> => {
    const certificates: ILocalCertificate[] = []

    /**
     * get the key store object
     */
    const oStore = await this.cadesplugin.CreateObjectAsync('CAdESCOM.Store')

    /**
     * open the storage, with certificates storage location as a parameter
     */
    await oStore.Open(storeName)

    /**
     * receive an object with all certificates from the storage
     */
    const CertificatesObj = await oStore.Certificates

    /**
     * receive the number of certificates
     */
    const certsCount = await CertificatesObj.Count

    /**
     * iterate through all certificates
     * fill the temporary array with data in format:
     * certificate serial number
     * certificate owner
     * creation date
     * expiration date
     */
    for (let i = 1; i <= certsCount; i++) {
      const certificate = await CertificatesObj.Item(i)
      const serialNumber = await certificate.SerialNumber
      const isExist = certs.find((findCert: ILocalCertificate) => findCert.serialNumber === serialNumber)

      if (isExist) continue

      const validToDate = await certificate.ValidToDate
      const subject = await certificate.SubjectName
      const validFromDate = await certificate.ValidFromDate
      const publicKey = await certificate.PublicKey()
      const algorithm = await publicKey.Algorithm
      const algorithmValue = await algorithm.Value

      certificates.push({
        algorithmValue,
        serialNumber,
        subject,
        validFromDate,
        validToDate
      })
    }

    await oStore.Close()
    return certificates
  }

  private readonly initializeHashedData = async (sHashValue: any, algorithm: number): Promise<any> => {
    // Create a CAdESCOM.HashedData object
    let oHashedData

    try {
      oHashedData = await this.cadesplugin.CreateObjectAsync('CAdESCOM.HashedData')
    } catch (e) {
      return e
    }

    // initialize the object with a pre-calculated hash value
    // the hashing algorithm must be specified before the hash value is passed
    await oHashedData.propset_Algorithm(algorithm)
    await oHashedData.SetHashValue(sHashValue)

    return oHashedData
  }

  private readonly CreateSignature = async (oCertificate: ILocalCertificate, oHashedData: any, enhanced?: boolean): Promise<any> => {
    // create a CAdESCOM.CPSigner object
    const oSigner = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner')
    oSigner.propset_Certificate(await this.getTargetCertificate(oCertificate.serialNumber))

    // create the CAdESCOM.CadesSignedData object
    const oSignedData = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData')

    let sSignedMessage = ''

    let type = this.cadesplugin.CADESCOM_CADES_BES;
    // let type = this.cadesplugin.CADESCOM_PKCS7_TYPE;

    if (enhanced) {
      oSigner.propset_TSAAddress('http://qs.cryptopro.ru/tsp/tsp.srf');
      type = this.cadesplugin.CADESCOM_CADES_X_LONG_TYPE_1;
    }
    // calculate the signature value
    try {
      sSignedMessage = await oSignedData.SignHash(oHashedData, oSigner, type)
    } catch (err) {
      const errCode = this.cadesplugin.getLastError(err) as string
      return {
        errCode,
        errMessage: `Ошибка создания подписи: ${errCode}`
      }
    }

    await this.VerifySignatureHash(oHashedData, sSignedMessage, type);
    return sSignedMessage
  }

  private readonly foundCertInTargetStore = async (certSerialNumber: string, storeName: number): Promise<any> => {
    const oStore = await this.cadesplugin.CreateObjectAsync('CAdESCOM.Store')

    await oStore.Open(storeName)

    const oCertificates = await oStore.Certificates
    const count = await oCertificates.Count
    let certificate

    for (let i = 1; i <= count; i++) {
      const currentCert = await oCertificates.Item(i)
      if ((await currentCert.SerialNumber) === certSerialNumber) {
        certificate = currentCert
      }
    }

    await oStore.Close()

    return certificate
  }

  public VerifySignatureHash = async (
    oHashedData: any,
    sSignedMessage: string,
    type: any
  ): Promise<any> => {
    const oSignedData = await this.cadesplugin.CreateObjectAsync(
      "CAdESCOM.CadesSignedData"
    );
    // Проверяем подпись
    try {
      await oSignedData.VerifyHash(oHashedData, sSignedMessage, type);
    } catch (err) {
      const errCode = this.cadesplugin.getLastError(err) as string;
      throw new Error(`Ошибка проверки подписи: ${errCode}`);
    }

    return true;
  };
}
